import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { api } from '../lib/api';
import { toast } from 'sonner';

const ST = { sent: 'bg-[#EBF3FA] text-[#13426B]', downloaded: 'bg-[#E6F4EA] text-[#0F5132]', expired: 'bg-secondary text-muted-foreground', email_failed: 'bg-[#FEE2E2] text-[#991B1B]' };

export const BundleShare = ({ tag, stageName, count, onClose }) => {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [shares, setShares] = useState([]);
  const load = () => api.get('/appeals/bundle/shares').then(({ data }) => setShares(data.shares.filter((s) => s.tag === tag))).catch(() => {});
  useEffect(() => { load(); }, [tag]); // eslint-disable-line

  const send = async () => {
    if (!email) return toast.error('Enter the clinic email');
    setBusy(true);
    try { await api.post('/appeals/bundle/share', { tag, email, note: note || null }); toast.success('Secure link emailed. It works once and expires in 7 days.'); setEmail(''); setNote(''); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-w-lg" data-testid="bundle-share-dialog">
        <DialogHeader>
          <DialogTitle style={{fontFamily: 'Lexend'}}>Send evidence bundle to your legal clinic</DialogTitle>
          <DialogDescription>{stageName} · {count} exhibit{count !== 1 ? 's' : ''}. The clinic gets a one-time secure link (expires in 7 days). Nothing else in your vault is shared.</DialogDescription>
        </DialogHeader>
        {count === 0 ? <p className="text-sm text-[#991B1B]" data-testid="bundle-share-empty">Tag documents to this stage first (My Documents → "Evidence for").</p> : (
          <div className="space-y-3">
            <div>
              <label htmlFor="clinic-email" className="text-sm font-medium block mb-1">Clinic or representative email</label>
              <Input id="clinic-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="intake@yourclinic.ca" className="h-11" data-testid="bundle-share-email" />
            </div>
            <div>
              <label htmlFor="clinic-note" className="text-sm font-medium block mb-1">Short message (optional, {300 - note.length} left)</label>
              <Textarea id="clinic-note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={2} placeholder="Hi, here are my documents for the SBT appeal. My hearing is in March." data-testid="bundle-share-note" />
            </div>
            <Button onClick={send} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90 w-full h-11" data-testid="bundle-share-send">{busy ? 'Sending…' : 'Email secure link'}</Button>
          </div>
        )}
        {shares.length > 0 && (
          <div className="mt-2" data-testid="bundle-share-list">
            <div className="text-xs font-semibold text-muted-foreground mb-1.5">Links sent for this stage</div>
            <ul className="space-y-1.5 text-sm">
              {shares.map((s) => <li key={s.created_at} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2" data-testid="bundle-share-item"><span className="truncate">{s.to_email} <span className="text-muted-foreground text-xs">· {s.created_at.slice(0, 10)}</span></span><Badge className={`border-0 ${ST[s.status]}`}>{s.status === 'sent' ? 'Awaiting download' : s.status === 'downloaded' ? 'Downloaded' : s.status === 'expired' ? 'Expired' : 'Email failed'}</Badge></li>)}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
